/**
 * Deal Utility Functions
 * Common utility functions for deal-related operations
 */

import {
  Deal,
  DealStatus,
  DealPriority,
  DealFilters,
} from '../types/deal.types'

// ============================================================================
// Formatting Utilities
// ============================================================================

/**
 * Format currency amount with proper symbol and formatting
 */
export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${currency} ${amount.toLocaleString()}`
  }
}

/**
 * Format date to readable string
 */
export function formatDate(
  date: string | Date,
  format: 'short' | 'long' | 'relative' = 'short',
  locale: string = 'en-US'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  if (format === 'relative') {
    return formatRelativeDate(dateObj)
  }

  const options: Intl.DateTimeFormatOptions =
    format === 'long'
      ? { year: 'numeric', month: 'long', day: 'numeric' }
      : { year: 'numeric', month: 'short', day: 'numeric' }

  return dateObj.toLocaleDateString(locale, options)
}

/**
 * Format date as relative time (e.g., "2 days ago", "in 3 weeks")
 */
export function formatRelativeDate(date: Date): string {
  const now = new Date()
  const diffInMs = date.getTime() - now.getTime()
  const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInDays === 0) return 'Today'
  if (diffInDays === 1) return 'Tomorrow'
  if (diffInDays === -1) return 'Yesterday'

  const absDiff = Math.abs(diffInDays)
  const unit = absDiff === 1 ? 'day' : 'days'

  if (diffInDays > 0) {
    return `in ${absDiff} ${unit}`
  } else {
    return `${absDiff} ${unit} ago`
  }
}

/**
 * Format percentage value
 */
export function formatPercentage(value: number, decimals: number = 0): string {
  return `${(value * 100).toFixed(decimals)}%`
}

/**
 * Format large numbers with abbreviations (e.g., 1.5M, 250K)
 */
export function formatCompactNumber(value: number): string {
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`
  }
  return value.toString()
}

// ============================================================================
// Deal Status and Priority Utilities
// ============================================================================

/**
 * Get display label for deal status
 */
export function getStatusLabel(status: DealStatus): string {
  const labels: Record<DealStatus, string> = {
    [DealStatus.OPEN]: 'Open',
    [DealStatus.IN_PROGRESS]: 'In Progress',
    [DealStatus.WON]: 'Won',
    [DealStatus.LOST]: 'Lost',
    [DealStatus.ON_HOLD]: 'On Hold',
    [DealStatus.ARCHIVED]: 'Archived',
  }
  return labels[status] || status
}

/**
 * Get display label for deal priority
 */
export function getPriorityLabel(priority: DealPriority): string {
  const labels: Record<DealPriority, string> = {
    [DealPriority.LOW]: 'Low',
    [DealPriority.MEDIUM]: 'Medium',
    [DealPriority.HIGH]: 'High',
    [DealPriority.URGENT]: 'Urgent',
  }
  return labels[priority] || priority
}

/**
 * Get color for deal status
 */
export function getStatusColor(status: DealStatus): string {
  const colors: Record<DealStatus, string> = {
    [DealStatus.OPEN]: '#007aff',
    [DealStatus.IN_PROGRESS]: '#ff9500',
    [DealStatus.WON]: '#34c759',
    [DealStatus.LOST]: '#ff3b30',
    [DealStatus.ON_HOLD]: '#8e8e93',
    [DealStatus.ARCHIVED]: '#c7c7cc',
  }
  return colors[status] || '#8e8e93'
}

/**
 * Get color for deal priority
 */
export function getPriorityColor(priority: DealPriority): string {
  const colors: Record<DealPriority, string> = {
    [DealPriority.LOW]: '#34c759',
    [DealPriority.MEDIUM]: '#007aff',
    [DealPriority.HIGH]: '#ff9500',
    [DealPriority.URGENT]: '#ff3b30',
  }
  return colors[priority] || '#8e8e93'
}

// ============================================================================
// Deal Calculation Utilities
// ============================================================================

/**
 * Calculate total value of deals
 */
export function calculateTotalValue(deals: Deal[]): number {
  return deals.reduce((total, deal) => total + (deal.amount || 0), 0)
}

/**
 * Calculate average deal value
 */
export function calculateAverageValue(deals: Deal[]): number {
  if (deals.length === 0) return 0
  return calculateTotalValue(deals) / deals.length
}

/**
 * Calculate weighted value based on probability
 */
export function calculateWeightedValue(deal: Deal): number {
  const amount = deal.amount || 0
  const probability = deal.probability || 0
  return amount * probability
}

/**
 * Calculate conversion rate
 */
export function calculateConversionRate(deals: Deal[]): number {
  if (deals.length === 0) return 0
  const wonDeals = deals.filter((d) => d.status === DealStatus.WON)
  return wonDeals.length / deals.length
}

/**
 * Calculate average time to close in days
 */
export function calculateAverageTimeToClose(deals: Deal[]): number {
  const closedDeals = deals.filter(
    (d) => d.status === DealStatus.WON && d.actualCloseDate && d.createdAt
  )

  if (closedDeals.length === 0) return 0

  const totalDays = closedDeals.reduce((total, deal) => {
    const created = new Date(deal.createdAt!)
    const closed = new Date(deal.actualCloseDate!)
    const days = Math.round(
      (closed.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
    )
    return total + days
  }, 0)

  return Math.round(totalDays / closedDeals.length)
}

// ============================================================================
// Deal Filtering and Sorting Utilities
// ============================================================================

/**
 * Apply filters to deals array
 */
export function filterDeals(deals: Deal[], filters: DealFilters): Deal[] {
  let filtered = [...deals]

  // Text search
  if (filters.search) {
    const searchLower = filters.search.toLowerCase()
    filtered = filtered.filter(
      (deal) =>
        deal.title.toLowerCase().includes(searchLower) ||
        deal.description?.toLowerCase().includes(searchLower)
    )
  }

  // Status filter
  if (filters.status && filters.status.length > 0) {
    filtered = filtered.filter((deal) => filters.status!.includes(deal.status!))
  }

  // Priority filter
  if (filters.priority && filters.priority.length > 0) {
    filtered = filtered.filter((deal) =>
      filters.priority!.includes(deal.priority!)
    )
  }

  // Pipeline filter
  if (filters.pipelineIds && filters.pipelineIds.length > 0) {
    filtered = filtered.filter((deal) =>
      filters.pipelineIds!.includes(deal.pipelineId!)
    )
  }

  // Stage filter
  if (filters.stageIds && filters.stageIds.length > 0) {
    filtered = filtered.filter((deal) =>
      filters.stageIds!.includes(deal.stageId!)
    )
  }

  // Owner filter
  if (filters.ownerIds && filters.ownerIds.length > 0) {
    filtered = filtered.filter((deal) =>
      filters.ownerIds!.includes(deal.ownerId)
    )
  }

  // Date range filter
  if (filters.dateRange) {
    const { start, end, field } = filters.dateRange
    const startDate = new Date(start)
    const endDate = new Date(end)

    filtered = filtered.filter((deal) => {
      let dateValue: string | undefined

      switch (field) {
        case 'created':
          dateValue = deal.createdAt
          break
        case 'updated':
          dateValue = deal.updatedAt
          break
        case 'expectedClose':
          dateValue = deal.expectedCloseDate
          break
        case 'actualClose':
          dateValue = deal.actualCloseDate
          break
      }

      if (!dateValue) return false

      const date = new Date(dateValue)
      return date >= startDate && date <= endDate
    })
  }

  // Amount range filter
  if (filters.amountRange) {
    const { min, max } = filters.amountRange
    filtered = filtered.filter((deal) => {
      const amount = deal.amount || 0
      return (!min || amount >= min) && (!max || amount <= max)
    })
  }

  // Tags filter
  if (filters.tags && filters.tags.length > 0) {
    filtered = filtered.filter((deal) =>
      deal.tags?.some((tag) => filters.tags!.includes(tag))
    )
  }

  return filtered
}

/**
 * Sort deals by multiple fields
 */
export function sortDeals(
  deals: Deal[],
  sorts: Array<{ field: string; direction: 'asc' | 'desc' }>
): Deal[] {
  if (sorts.length === 0) return deals

  return [...deals].sort((a, b) => {
    for (const sort of sorts) {
      const aValue = getNestedValue(a, sort.field)
      const bValue = getNestedValue(b, sort.field)

      if (aValue === bValue) continue

      const comparison = compareValues(aValue, bValue)
      if (comparison !== 0) {
        return sort.direction === 'asc' ? comparison : -comparison
      }
    }
    return 0
  })
}

/**
 * Group deals by a field
 */
export function groupDeals(
  deals: Deal[],
  groupBy: string
): Record<string, Deal[]> {
  const grouped: Record<string, Deal[]> = {}

  for (const deal of deals) {
    const key = String(getNestedValue(deal, groupBy) || 'Other')
    if (!grouped[key]) {
      grouped[key] = []
    }
    grouped[key].push(deal)
  }

  return grouped
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

/**
 * Compare two values for sorting
 */
function compareValues(a: any, b: any): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1

  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b)
  }

  if (a < b) return -1
  if (a > b) return 1
  return 0
}

/**
 * Generate a unique deal ID
 */
export function generateDealId(): string {
  return `deal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Validate deal data
 */
export function validateDeal(deal: Partial<Deal>): string[] {
  const errors: string[] = []

  if (!deal.title || deal.title.trim() === '') {
    errors.push('Title is required')
  }

  if (deal.amount && deal.amount < 0) {
    errors.push('Amount must be positive')
  }

  if (deal.probability && (deal.probability < 0 || deal.probability > 1)) {
    errors.push('Probability must be between 0 and 1')
  }

  if (deal.expectedCloseDate && deal.actualCloseDate) {
    const expected = new Date(deal.expectedCloseDate)
    const actual = new Date(deal.actualCloseDate)
    if (actual < expected) {
      errors.push('Actual close date cannot be before expected close date')
    }
  }

  return errors
}
